// ============================================================================
// Message Types
// ============================================================================

/**
 * Message types for iframe communication between Tellet chat and parent page
 * @see backend/libs/common/src/types/conversation.types.ts for backend type alignment
 */
export enum MessageType {
  // Legacy message types (backward compatibility)
  INPUT = "INPUT",
  OUTPUT = "OUTPUT",
  END = "END",

  // New enhanced message types
  STATUS_CHANGE = "STATUS_CHANGE",
  CONVERSATION_COMPLETE = "CONVERSATION_COMPLETE",
  DIGEST_READY = "DIGEST_READY",
}

/**
 * Base interface for all iframe messages
 */
export interface IframeMessage {
  type: MessageType;
  data?: Record<string, any>;
}

// ============================================================================
// Conversation Status (aligned with backend)
// ============================================================================

/**
 * Conversation status enum - aligned with backend ConversationStatus
 * @see backend/libs/common/src/types/conversation.types.ts
 */
export enum ConversationStatus {
  TEST = "test",
  SANDBOX = "sandbox",
  PRESCREENING = "prescreening",
  PRESCREENING_FAILED = "prescreening_failed",
  SANDBOX_PRESCREENING_FAILED = "sandbox_prescreening_failed",
  INITIATED = "initiated",
  IN_PROGRESS = "in_progress",
  DONE = "done",
  ABANDONED = "abandoned",
  ANALYZING = "analyzing",
  DIGESTED = "digested",
  TEST_DONE = "test_done",
  SANDBOX_DONE = "sandbox_done",
  DELETED = "deleted",
  RESETTING = "resetting",
}

// ============================================================================
// Enhanced Message Data Interfaces
// ============================================================================

/**
 * Progress information for status change events
 */
export interface ProgressInfo {
  count: number;
  limit: number;
  done: boolean;
}

/**
 * Data sent with STATUS_CHANGE message
 * Sent whenever conversation status changes
 */
export interface StatusChangeData {
  status: ConversationStatus;
  conversationId: string;
  timestamp: string; // ISO 8601 format
  progress: ProgressInfo;
}

/**
 * Message format for iframe communication
 * Simplified version of backend Message type, formatted for parent page consumption
 * @see backend/libs/common/src/types/conversation.types.ts Message class
 */
export interface IframeMessageData {
  role: "user" | "assistant";
  text: string; // sanitized HTML or transcription text
  created_at: string; // ISO 8601 format
  inputMethod: "text" | "speech" | "video" | "image";
  hasMedia: boolean;
  mediaType?: "audio" | "video" | "image";
  // Note: Media URLs are NOT included for security/size reasons
}

/**
 * Conversation metadata (sanitized for iframe communication)
 * @see backend/libs/common/src/types/conversation.types.ts ConversationMetadata
 */
export interface ConversationMetadata {
  participant_id?: string;
  language?: string;
  source?: string;
  [key: string]: string | number | undefined;
  // Sensitive fields (email, password, token, etc.) are removed during sanitization
}

/**
 * Data sent with CONVERSATION_COMPLETE message
 * Sent when interview completes (status becomes DONE)
 */
export interface ConversationCompleteData {
  conversationId: string;
  status: ConversationStatus;
  metadata: ConversationMetadata;
  messages: IframeMessageData[];
  finishedAt: string; // ISO 8601 format
  createdAt: string; // ISO 8601 format
  startedAt: string; // ISO 8601 format
  question_index: number;
  digest?: {
    // Optional: only present when status is DIGESTED
    summary: string;
    themes: string[];
    opportunities: string[];
    quotes: DigestedQuote[];
  };
  error?: string; // Present if data fetch failed
}

/**
 * Digested quote with importance score
 * @see backend/libs/common/src/types/conversation.types.ts DigestedQuote
 */
export interface DigestedQuote {
  quote: string;
  score: number; // 1-100
}

/**
 * Data sent with DIGEST_READY message
 * Sent when conversation analysis completes (status becomes DIGESTED)
 * @see backend/libs/common/src/types/conversation.types.ts ConversationDigest
 */
export interface DigestReadyData {
  conversationId: string;
  summary: string;
  themes: string[];
  opportunities: string[];
  quotes: DigestedQuote[];
}

// ============================================================================
// Helper Functions
// ============================================================================

export const isInIframe = (): boolean => {
  return window.parent !== window && window.top !== window;
};

export const sendMessageToParent = (message: IframeMessage): void => {
  if (isInIframe()) {
    window.parent.postMessage(message, "*");
  }
};

export const sendInputMessageToParent = (data: Record<string, any>): void => {
  sendMessageToParent({
    type: MessageType.INPUT,
    data,
  });
};

/**
 * Send STATUS_CHANGE message to parent page
 * Called whenever conversation status changes
 * @param data Status change information
 */
export const sendStatusChangeToParent = (data: StatusChangeData): void => {
  if (!data.conversationId || !data.status) {
    console.warn(
      "[iframeUtils] sendStatusChangeToParent: Missing required fields",
      data,
    );
    return;
  }

  sendMessageToParent({
    type: MessageType.STATUS_CHANGE,
    data,
  });
};

/**
 * Send CONVERSATION_COMPLETE message to parent page
 * Called when interview completes (status becomes DONE)
 * Note: Also sends legacy END message for backward compatibility
 * @param data Complete conversation information
 */
export const sendConversationCompleteToParent = (
  data: ConversationCompleteData,
): void => {
  if (!data.conversationId) {
    console.warn(
      "[iframeUtils] sendConversationCompleteToParent: Missing conversationId",
      data,
    );
    return;
  }

  // Send legacy END message first for backward compatibility
  sendMessageToParent({ type: MessageType.END });

  // Then send enhanced CONVERSATION_COMPLETE message
  sendMessageToParent({
    type: MessageType.CONVERSATION_COMPLETE,
    data,
  });
};

/**
 * Send DIGEST_READY message to parent page
 * Called when conversation analysis completes (status becomes DIGESTED)
 * @param data Digest information (summary, themes, opportunities, quotes)
 */
export const sendDigestReadyToParent = (data: DigestReadyData): void => {
  if (!data.conversationId || !data.summary) {
    console.warn(
      "[iframeUtils] sendDigestReadyToParent: Missing required fields",
      data,
    );
    return;
  }

  sendMessageToParent({
    type: MessageType.DIGEST_READY,
    data,
  });
};

// Store data received from parent
let parentData: Record<string, any> = {};

// Handle messages from parent (OUTPUT messages)
export const handleParentMessage = (event: MessageEvent): void => {
  // Parent sends data as a flat object (not wrapped in IframeMessage structure)
  if (event.data && typeof event.data === "object") {
    parentData = { ...parentData, ...event.data };
  }
};

// Get data that was sent from parent
export const getParentData = (key: string): any => {
  // Notify parent that we are requesting data
  sendMessageToParent({ type: MessageType.OUTPUT });
  return parentData[key] || null;
};

// Encode and decode keys to avoid issues with special characters when using setAttribute in iframes
export function encodeKey(key: string): string {
  return key.replace(/\//g, "__slash__");
}

export function decodeKey(encoded: string): string {
  return encoded.replace(/__slash__/g, "/");
}

// Initialize message listener
if (typeof window !== "undefined") {
  window.addEventListener("message", handleParentMessage);
}
